import { ChangeDetectionStrategy, ChangeDetectorRef, Component, InputSignal, inject, input } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

import { provideValueAccessor } from '@core/shared/util';
import { INPUT_SUPPORTED_FILE_TYPE_EXTENSIONS } from '@mp/shared/helper-links/zendesk-support-dialog/util';

import { FileUploadButtonComponent } from '../file-upload-button/file-upload-button.component';

import { AttachmentTypeIconPipe } from './attachment-type-icon.pipe';
import { SupportMessageAttachmentsPresenter } from './support-message-attachments.presenter';

@Component({
  selector: 'mp-support-message-attachments',
  templateUrl: './support-message-attachments.component.html',
  styleUrl: './support-message-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatIconButton, AttachmentTypeIconPipe, FileUploadButtonComponent],
  providers: [SupportMessageAttachmentsPresenter, provideValueAccessor(SupportMessageAttachmentsComponent)],
})
export class SupportMessageAttachmentsComponent implements ControlValueAccessor {
  private readonly presenter: SupportMessageAttachmentsPresenter = inject(SupportMessageAttachmentsPresenter);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly maxAttachmentsNumber: InputSignal<number> = input.required<number>();

  readonly maxFileSize: InputSignal<number> = input.required<number>();

  protected attachments!: File[];

  readonly INPUT_SUPPORTED_FILE_TYPE_EXTENSIONS: string = INPUT_SUPPORTED_FILE_TYPE_EXTENSIONS;

  addAttachment(fileList: FileList): void {
    const filesArray: File[] = Array.from(fileList);
    const attachmentsRemainingLimit: number = this.maxAttachmentsNumber() - this.attachments.length;

    if (!this.presenter.validateAttachments(filesArray, this.maxFileSize(), attachmentsRemainingLimit)) {
      return;
    }

    this.attachments = [...this.attachments, ...filesArray];
    this.onChange(this.attachments);
  }

  deleteAttachment(selectedAttachment: File): void {
    this.attachments = this.attachments.filter((attachment) => attachment !== selectedAttachment);
    this.onChange(this.attachments);
  }

  writeValue(attachments: File[]): void {
    this.attachments = attachments;

    this.cdr.markForCheck();
  }

  onChange = (_value: File[]) => {};

  onTouched = () => {};

  registerOnChange(onChange: (value: File[]) => void) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
}
